/* Form-family park: the legacy combobox retires with its last parked callers, in the Table and details-tab rounds (component-map.md). */
import { Box, useFormControl, useMergeRefs } from '@chakra-ui/react';
import { Text } from '@main/complykit/components/text';
import {
  chakraComponents,
  CoercedMenuPlacement,
  GroupBase,
  MenuListProps,
  MenuProps,
  PropsValue,
  SelectComponentsConfig,
} from 'chakra-react-select';
import {
  JSXElementConstructor,
  MouseEvent,
  MutableRefObject,
  ReactNode,
  TouchEvent,
  useCallback,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';

import { editablePopoverInternalPortalAttribute } from '../editable/editable-popover';
import type { ComboboxProps } from './combobox';
import { isNewOption } from './new-option';
import { isCreatableSelectProps } from './utils';
import { VirtualizedOptions } from './virtualized-options';

type Value<TOption> = JSXElementConstructor<
  TOption & {
    menuIsOpen?: boolean;
    removeProps?: JSX.IntrinsicElements['div'];
  }
>;

export type ComboboxComponents<TOption> = {
  Option?: Value<TOption>;
  SelectedOption?: Value<TOption>;
  OptionContainer?: JSXElementConstructor<{
    children: ReactNode;
    menuIsOpen: boolean;
    values: PropsValue<TOption>;
    groupSize?: number;
  }>;
  Group?: JSXElementConstructor<{ label: string; children: ReactNode; index: number }>;
  MenuList?: JSXElementConstructor<{
    children: ReactNode;
    innerRef: React.Ref<HTMLDivElement>;
    innerProps: JSX.IntrinsicElements['div'];
    maxHeight: number;
  }>;
};

type MenuPlacement = 'top' | 'bottom';
type SelectPropsWithResolvedPlacement = {
  resolvedMenuPlacement?: MenuPlacement;
  onMenuPlacementChange?: (placement: MenuPlacement) => void;
};

type MenuInnerProps = MenuProps<object, boolean, GroupBase<object>>['innerProps'];

function getResolvedMenuPlacement(selectProps: unknown): MenuPlacement {
  const props = selectProps as SelectPropsWithResolvedPlacement;
  return props.resolvedMenuPlacement === 'top' ? 'top' : 'bottom';
}

/**
 * react-select closes an open menu when mousedown hits anything except the
 * input. Selected values leave that input ~2px wide, so creatable typing is
 * easy to lose and hard to recover. Focus the input instead; leave already-
 * interactive targets (remove/clear) alone via their preventDefault.
 */
function focusComboboxInputOnControlPress(
  event: MouseEvent<HTMLDivElement> | TouchEvent<HTMLDivElement>,
  menuIsOpen: boolean | undefined,
  openMenuOnClick: boolean | undefined,
  onMenuOpen: (() => void) | undefined,
) {
  if (event.defaultPrevented) {
    return;
  }
  if ('button' in event && event.button !== 0) {
    return;
  }

  const target = event.target;
  if (!(target instanceof HTMLElement)) {
    return;
  }

  const isTextField = target.tagName === 'INPUT' || target.tagName === 'TEXTAREA';
  if (!isTextField) {
    event.preventDefault();
  }

  const input = event.currentTarget.querySelector('input');
  input?.focus();

  if (!menuIsOpen && openMenuOnClick !== false) {
    onMenuOpen?.();
  }
}

function VirtualizedMenuList<TOption, IsMulti extends boolean, TGroup extends GroupBase<TOption>>({
  customMenuList: CustomMenuList,
  isCreatable,
  labelledBy,
  suppressFocusScroll,
  ...menuProps
}: MenuListProps<TOption, IsMulti, TGroup> & {
  customMenuList?: ComboboxComponents<TOption>['MenuList'];
  isCreatable: boolean;
  labelledBy?: string;
  suppressFocusScroll: MutableRefObject<boolean>;
}) {
  const [scrollElement, setScrollElement] = useState<HTMLDivElement | null>(null);
  const setScrollElementRef = useCallback((element: HTMLDivElement | null) => {
    setScrollElement(element);
  }, []);
  const innerRef = useMergeRefs(setScrollElementRef, menuProps.innerRef);
  const placement = getResolvedMenuPlacement(menuProps.selectProps);
  const { t } = useTranslation('ui');

  const children = [
    isCreatable && placement === 'bottom' ? (
      <MenuListHint key="create-hint-bottom" placement={placement}>
        {t('combobox.selectOrCreateOption')}
      </MenuListHint>
    ) : null,
    menuProps.children,
    isCreatable && placement === 'top' ? (
      <MenuListHint key="create-hint-top" placement={placement}>
        {t('combobox.selectOrCreateOption')}
      </MenuListHint>
    ) : null,
  ];
  const virtualizedOptions = (
    <VirtualizedOptions
      focusedOption={menuProps.focusedOption}
      scrollElement={scrollElement}
      selectionCount={menuProps.getValue().length}
      suppressFocusScroll={suppressFocusScroll}
      viewportHeight={menuProps.maxHeight}
    >
      {children}
    </VirtualizedOptions>
  );

  if (CustomMenuList) {
    return (
      <CustomMenuList
        innerRef={innerRef}
        innerProps={menuProps.innerProps}
        maxHeight={menuProps.maxHeight}
      >
        {virtualizedOptions}
      </CustomMenuList>
    );
  }

  return (
    <chakraComponents.MenuList
      {...menuProps}
      innerRef={innerRef}
      innerProps={{
        ...menuProps.innerProps,
        role: 'listbox',
        'aria-labelledby': labelledBy,
      }}
    >
      {virtualizedOptions}
    </chakraComponents.MenuList>
  );
}

export function useComboboxComponents<
  TOption extends object,
  IsMulti extends boolean,
  TGroup extends GroupBase<TOption>,
>(
  {
    hideClearIndicator,
    isClearable = true,
    groupSize,
    ...props
  }: ComboboxProps<TOption, IsMulti, TGroup>,
  suppressFocusScroll: MutableRefObject<boolean>,
) {
  const { id } = useFormControl({});
  const labelledBy = props['aria-labelledby'] ?? (id ? `${id}-label` : undefined);
  const isCreatable = isCreatableSelectProps(props);
  const Option = props.components?.Option;
  const SelectedOption = props.components?.SelectedOption;
  const OptionContainer = props.components?.OptionContainer;
  const CustomGroup = props.components?.Group;
  const CustomMenuList = props.components?.MenuList;

  return useMemo<SelectComponentsConfig<TOption, IsMulti, TGroup>>(
    () => ({
      DropdownIndicator: () => null,
      ...(hideClearIndicator === true && { ClearIndicator: () => null }),
      IndicatorSeparator: () => null,
      Control: (controlProps) => {
        const onControlPress = (event: MouseEvent<HTMLDivElement> | TouchEvent<HTMLDivElement>) => {
          focusComboboxInputOnControlPress(
            event,
            controlProps.selectProps.menuIsOpen,
            controlProps.selectProps.openMenuOnClick,
            controlProps.selectProps.onMenuOpen,
          );
        };

        return (
          <chakraComponents.Control
            {...controlProps}
            innerProps={{
              ...controlProps.innerProps,
              onMouseDown: onControlPress,
              onTouchEnd: onControlPress,
            }}
          />
        );
      },
      Menu: (menuProps) => {
        const selectProps = menuProps.selectProps as typeof menuProps.selectProps &
          SelectPropsWithResolvedPlacement;
        const placement: MenuPlacement = menuProps.placement === 'top' ? 'top' : 'bottom';

        if (selectProps.resolvedMenuPlacement !== placement) {
          queueMicrotask(() => selectProps.onMenuPlacementChange?.(placement));
        }

        const innerProps = {
          ...menuProps.innerProps,
          [editablePopoverInternalPortalAttribute]: '',
        } as MenuInnerProps;

        return <chakraComponents.Menu {...menuProps} innerProps={innerProps} />;
      },
      ...(CustomGroup && {
        Group: (groupProps) => {
          const index = groupProps.selectProps.options.findIndex(
            (optionOrGroup) => optionOrGroup === groupProps.data,
          );

          return (
            <CustomGroup label={groupProps.label as string} index={index}>
              {groupProps.children}
            </CustomGroup>
          );
        },
      }),
      MenuList: (menuProps) => (
        <VirtualizedMenuList
          {...menuProps}
          customMenuList={CustomMenuList}
          isCreatable={isCreatable}
          labelledBy={labelledBy}
          suppressFocusScroll={suppressFocusScroll}
        />
      ),
      MultiValue: (props) => {
        const CustomOption = SelectedOption ?? Option;

        if (CustomOption) {
          const { data, selectProps, removeProps } = props;
          return (
            <CustomOption
              {...data}
              menuIsOpen={selectProps.menuIsOpen}
              removeProps={
                isClearable && selectProps.menuIsOpen && !selectProps.isDisabled
                  ? removeProps
                  : undefined
              }
            />
          );
        }

        return <chakraComponents.MultiValue {...props} />;
      },
      Option: ({ children, ...props }) => (
        <chakraComponents.Option {...props}>
          {isNewOption(props.data) ? <NewOptionPrefix /> : null}
          {Option ? <Option {...props.data} /> : children}
        </chakraComponents.Option>
      ),
      ValueContainer: (props) => {
        if (OptionContainer) {
          return (
            <chakraComponents.ValueContainer {...props}>
              <OptionContainer
                menuIsOpen={props.selectProps.menuIsOpen}
                values={props.selectProps.value}
                groupSize={groupSize}
              >
                {props.children}
              </OptionContainer>
            </chakraComponents.ValueContainer>
          );
        }

        return <chakraComponents.ValueContainer {...props} />;
      },
      SelectContainer: (props) => {
        const testId = props.selectProps['data-testid' as keyof typeof props.selectProps];
        const innerProps = testId
          ? { ...props.innerProps, 'data-testid': testId }
          : props.innerProps;
        return <chakraComponents.SelectContainer {...props} innerProps={innerProps} />;
      },
    }),
    [
      hideClearIndicator,
      isCreatable,
      SelectedOption,
      Option,
      isClearable,
      OptionContainer,
      groupSize,
      CustomGroup,
      CustomMenuList,
      suppressFocusScroll,
      labelledBy,
    ],
  );
}

function MenuListHint({
  children,
  placement,
}: {
  children: ReactNode;
  placement: CoercedMenuPlacement;
}) {
  return (
    <Box
      px={3}
      pt={placement === 'bottom' ? 3 : 2}
      pb={placement === 'top' ? 3 : 2}
      mt={placement === 'top' ? 2 : 0}
      borderTopWidth={placement === 'top' ? '1px' : 0}
      borderColor="chakra-border-color"
    >
      <Text size="xs" color="muted">
        {children}
      </Text>
    </Box>
  );
}

function NewOptionPrefix() {
  const { t } = useTranslation('ui');
  return (
    <Box mr={2}>
      <Text size="sm">{t('combobox.create')}</Text>
    </Box>
  );
}
